import React, { useEffect, useState } from 'react';
import { Text, Pressable, HStack, VStack, Button, View, Box } from 'native-base';
import { ActivityIndicator, TextInput } from 'react-native';
import { useSendMessage } from '../hooks/useSendMessage';
import Mustache from 'mustache';
import { faArrowCircleUp } from '@fortawesome/pro-solid-svg-icons/faArrowCircleUp';
import { faWandSparkles } from '@fortawesome/pro-solid-svg-icons/faWandSparkles';
import { Icon } from '../../Common/components/Icon';
import { useQuickReplies } from '../../Request/hooks/useQuickReplies';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons/faCalendar';
import { isDoctorPortal } from '../../Common/utils/isDoctorPortal';
import { faKeyboard } from '@fortawesome/pro-solid-svg-icons/faKeyboard';
import { SendBookingLink } from './SendBookingLink';
import { faKeyboardDown } from '@fortawesome/pro-solid-svg-icons/faKeyboardDown';
import { faPhone } from '@fortawesome/pro-solid-svg-icons/faPhone';
import { faMessage } from '@fortawesome/pro-solid-svg-icons/faMessage';
import { IHStackProps } from 'native-base/lib/typescript/components/primitives/Stack/HStack';

export const ComposeMessage = ({
  ThreadID,
  ComposeBoxStyle,
  Brand,
  CustomerID,
  ThreadChannel,
  onSent,
}: {
  ThreadID: string;
  CustomerID?: string;
  Brand: string;
  ThreadChannel: string;
  ComposeBoxStyle: IHStackProps;
  onSent?: () => void;
}) => {
  const [message, setMessage] = useState('');

  const { sendMessage, sending } = useSendMessage(ThreadID);

  const { showQuickReply, replies, repliesKeys } = useQuickReplies(Brand);

  const [channel, setChannel] = useState<'app' | 'sms'>('app');

  const [showSendBookingLink, setShowSendBookingLink] = useState(false);

  const LargeCompose = isDoctorPortal();

  const [showReplies, setShowReplies] = useState(false);

  useEffect(() => {
    setMessage('');
  }, [ThreadID]);

  const send = async () => {
    if (message.length === 0) return;

    await sendMessage(message, channel);

    setMessage('');
    onSent?.();
  };

  const onQuickReplyPressed = (reply: string) => {
    setShowReplies(false);

    if (!reply) return;

    const quickReplyMessage = replies?.[reply];

    if (!quickReplyMessage) return;

    if (reply.startsWith('+')) {
      setMessage(`${message}\n\n${quickReplyMessage}`.trim());
    } else {
      const renderedMessage = Mustache.render(quickReplyMessage, {
        given: '',
        name: '',
      });

      setMessage(renderedMessage, channel);
    }
  };

  const appendBookingLink = (bookingLink: string) => {
    setShowReplies(false);
    setShowSendBookingLink(false);

    if (bookingLink) {
      setMessage(`${message}\n\n${bookingLink}`.trim());
    }
  };

  const smsMessages = Math.ceil(message?.length / 160);

  const notAbleToSMS = ['instagram', 'messenger', 'email'];

  const canSMS = !(notAbleToSMS.includes(ThreadChannel ?? '') ?? false);

  return (
    <VStack mt={2} space={2}>
      {showSendBookingLink && (
        <SendBookingLink
          customerId={ThreadID}
          close={() => setShowReplies(false)}
          setMessage={appendBookingLink}
        />
      )}

      {showQuickReply && (
        <>
          <HStack space={1} flexWrap={'wrap'} alignItems={'center'}>
            <Button variant={'outline'} onPress={() => setShowReplies(!showReplies)} size={'xs'}>
              <Icon icon={showReplies ? faKeyboardDown : faKeyboard} size={'sm'} />
            </Button>

            <Box flex={1} />

            {channel === 'sms' && message?.length > 0 && (
              <Text
                color={smsMessages > 1 ? 'red.500' : 'black'}
                fontSize={'xs'}
              >{`Cost: ${smsMessages} SMS messages`}</Text>
            )}

            {canSMS && (
              <HStack bg={'gray.50'} borderRadius={'full'} overflow={'hidden'}>
                <Pressable
                  onPress={() => setChannel('app')}
                  px={3}
                  py={1.5}
                  bg={channel === 'app' ? 'gray.200' : null}
                >
                  <HStack space={1} alignItems={'center'}>
                    <Icon icon={faMessage} size={'xs'} color={'black'} />
                    <Text fontWeight={channel === 'app' ? 600 : null} fontSize={'xs'}>
                      Portal
                    </Text>
                  </HStack>
                </Pressable>
                <Pressable
                  px={3}
                  onPress={() => setChannel('sms')}
                  py={1.5}
                  bg={channel === 'sms' ? 'gray.200' : null}
                >
                  <HStack space={1} alignItems={'center'}>
                    <Icon icon={faPhone} size={'xs'} color={'black'} />
                    <Text fontWeight={channel === 'sms' ? 600 : null} fontSize={'xs'}>
                      SMS
                    </Text>
                  </HStack>
                </Pressable>
              </HStack>
            )}
          </HStack>
        </>
      )}

      {showReplies && (
        <VStack space={2} borderRadius={'sm'} bg={'gray.50'} p={2}>
          <Text fontWeight={600}>Quick Replies</Text>
          <HStack space={1} flexWrap={'wrap'}>
            {repliesKeys.map((a) => (
              <Button
                mb={2}
                leftIcon={<Icon icon={faWandSparkles} color={'black'} />}
                variant={'outline'}
                onPress={() => onQuickReplyPressed(a)}
                size={'xs'}
                key={a}
              >
                {a?.toUpperCase()}
              </Button>
            ))}
          </HStack>
          {CustomerID && (
            <Button
              mb={2}
              leftIcon={<Icon icon={faCalendar} color={'black'} />}
              variant={'outline'}
              onPress={() => {
                setShowSendBookingLink(true);
                setShowReplies(false);
              }}
              size={'xs'}
            >
              Send Booking Link
            </Button>
          )}
        </VStack>
      )}

      <HStack
        bg="white"
        borderWidth={1}
        borderRadius={'md'}
        borderColor={'gray.300'}
        {...ComposeBoxStyle}
      >
        <TextInput
          style={{
            minHeight: LargeCompose ? 150 : 100,
            flex: 1,
            padding: 10,
            marginTop: 10,
          }}
          value={message}
          multiline={true}
          placeholder={'Type your message to send'}
          editable={!sending}
          placeholderTextColor={'grey'}
          onChangeText={(text) => setMessage(text)}
        />

        <Pressable
          onPress={send}
          disabled={sending}
          p={5}
          w={24}
          borderLeftWidth={1}
          borderLeftColor={'gray.400'}
          bg={'gray.320'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          {sending && <ActivityIndicator />}

          {!sending && (
            <View flexDirection="row" flexWrap="nowrap">
              <Text
                color={'rgb(12, 121, 236)'}
                fontWeight={600}
                flexDirection="row"
                flexWrap="nowrap"
                mr="2"
              >
                Send
              </Text>
              <Icon color={'rgb(12, 121, 236)'} icon={faArrowCircleUp} />
            </View>
          )}
        </Pressable>
      </HStack>
    </VStack>
  );
};
